import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { PhoneInput } from 'react-international-phone'
import { achatNumeroEnAttente, ApiHttpError, fetchProfile, updateUserProfile } from '@/api/auth'
import { PhoneVerification, type EtapeCode } from '@/components/onboarding/PhoneVerification'
import { finalizeOnboarding } from '@/api/onboarding'
import { UpdateCardDialog } from '@/components/manager/subscription/UpdateCardDialog'
import { TrialAlreadyUsedError, startTrialWithRetry } from '@/api/stripe'
import { OnboardingNavyShell } from '@/components/onboarding/NavyShell'
import { Avancement } from '@/pages/onboarding/MiseEnService'
import { ApercuNumero, type EtatTextos } from '@/pages/onboarding/Apercus'
import { useCompanyPhoneNumbers } from '@/api/company'
import { useFirstCompany } from '@/api/profile'
import { defaultLocale } from '@/lib/locales'

interface LocationState {
  fromCheckout?: boolean
}

const schema = z.object({
  firstName: z.string().min(1, 'Prénom requis'),
  lastName: z.string().optional(),
  // Numéro obligatoire — on contrôle juste qu'il y a au moins 8 chiffres
  // (format vraiment validé côté Twilio Lookup plus tard si besoin).
  personalPhone: z
    .string()
    .min(1, 'Numéro requis')
    .refine(
      (v) => v.replace(/\D/g, '').length >= 8,
      'Numéro de téléphone invalide',
    ),
})

type Form = z.infer<typeof schema>

function formatNumber(n: string) {
  const digits = n.replace(/\D/g, '')
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return n
}

export function OnboardingWelcomePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // `fromCheckout` peut venir de deux sources :
  // 1. location.state.fromCheckout (cas onSuccess JS sans 3DS — Checkout.tsx
  //    navigate avec state)
  // 2. query params Stripe sur le return_url (cas 3DS qui force un redirect
  //    full-page → le state React n'existe pas). On détecte via les params
  //    posés par Stripe : redirect_status, payment_intent, session_id.
  const fromCheckout = (() => {
    if ((location.state as LocationState | null)?.fromCheckout) return true
    const params = new URLSearchParams(location.search)
    return (
      params.get('redirect_status') === 'succeeded' ||
      params.has('payment_intent') ||
      params.has('session_id')
    )
  })()

  // Trial with a 3DS redirect: Stripe comes back with ?setup_intent=seti_…. The
  // trial starts here, before the number is bought (the webhook does it too;
  // the server keeps a single subscription).
  const setupIntentRetour = (() => {
    const params = new URLSearchParams(location.search)
    return params.get('redirect_status') === 'succeeded' ? params.get('setup_intent') : null
  })()
  const [essaiPret, setEssaiPret] = useState(!setupIntentRetour)
  useEffect(() => {
    if (!setupIntentRetour) return
    let cancelled = false
    void startTrialWithRetry(setupIntentRetour)
      .catch((err) => {
        // Trial already used: back to the plans, where the checkout bills right away.
        if (err instanceof TrialAlreadyUsedError && !cancelled) navigate(`/${locale}/subscribe`, { replace: true })
      })
      .finally(() => {
        if (!cancelled) setEssaiPret(true)
      })
    return () => {
      cancelled = true
    }
  }, [setupIntentRetour, navigate, locale])

  // The `essai_demarre` conversion is sent by the server when Stripe confirms the
  // card (webhook): from the browser it was lost 5 times out of 6 (blockers, tab closed).

  const [callizyNumber, setCallizyNumber] = useState('')
  const { data: compagnie } = useFirstCompany()
  const [error, setError] = useState('')
  const [carteManquante, setCarteManquante] = useState(false)
  const [carteOuverte, setCarteOuverte] = useState(false)
  const finalizedRef = useRef(false)

  // Cellulaire vérifié par code texto : exigé pour les comptes récents avant l'achat du numéro.
  // null = profil pas encore lu ; true = à faire ; false = déjà fait ou pas concerné.
  const [verifRequise, setVerifRequise] = useState<boolean | null>(null)
  const [etapeCode, setEtapeCode] = useState<EtapeCode>('idle')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', personalPhone: '' },
  })

  const personalPhone = watch('personalPhone')

  // Charge le profil + numéro existant (si déjà finalisé)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const profile = await fetchProfile()
        if (cancelled) return
        if (profile.first_name) setValue('firstName', profile.first_name)
        if (profile.last_name) setValue('lastName', profile.last_name)
        const requise = !!profile.phone_verification_required
        setVerifRequise(requise)
        if (!requise && profile.phone_verified_at && profile.phone_number) {
          setValue('personalPhone', profile.phone_number)
          setEtapeCode('ok')
        }
      } catch (err) {
        console.error('[Welcome] profile fetch failed:', err)
        setVerifRequise(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [setValue])

  // Si on arrive de Checkout, déclenche finalize (achat numéro Twilio)
  const finalizeMutation = useMutation({
    mutationFn: async () => {
      const isDev = import.meta.env.DEV
      return finalizeOnboarding(isDev)
    },
    onSuccess: (data) => {
      // L'entreprise en cache dit encore « numéro à acheter » : sans relecture,
      // le garde ramènerait ici.
      void queryClient.invalidateQueries({ queryKey: ['companies'] })
      if (data.number_unavailable) {
        // Taken in the meantime, or refused by the provider: the server cleared
        // the choice. Back to choosing, in the same area code.
        const lieu = data.indicatif ? `&lieu=${data.indicatif}` : ''
        navigate(`/${locale}/onboarding/step1?indisponible=1${lieu}`, { replace: true })
        return
      }
      if (data.phone_number) setCallizyNumber(data.phone_number)
    },
    onError: (err) => {
      const reponse = isAxiosError(err) ? err.response : undefined
      const code = (reponse?.data as { error?: string } | undefined)?.error
      // A trial without a card: the card is added here, then the number is bought.
      if (reponse?.status === 402 && code === 'payment_method_required') {
        setCarteManquante(true)
        return
      }
      // Another door (the card added a minute ago) is buying it right now.
      if (reponse?.status === 409 && code === 'achat_en_cours') {
        window.setTimeout(() => finalizeMutation.mutate(), 3000)
        return
      }
      console.error('[Welcome] finalize failed:', err)
      const message =
        err instanceof ApiHttpError
          ? (err.data as { error?: string })?.error || `HTTP ${err.status}`
          : 'Impossible de finaliser. Réessayez ou contactez le support.'
      setError(message)
    },
  })

  // Revenu plus tard (onglet fermé, reconnexion) : on n'arrive plus de Stripe,
  // mais le numéro payé reste à acheter.
  const doitAcheter = fromCheckout || achatNumeroEnAttente(compagnie)

  useEffect(() => {
    if (!doitAcheter || !essaiPret || finalizedRef.current) return
    // Le numéro n'est acheté qu'une fois le cellulaire vérifié (ou si le compte n'y est pas tenu).
    if (verifRequise === null || (verifRequise && etapeCode !== 'ok')) return
    finalizedRef.current = true
    finalizeMutation.mutate()
  }, [doitAcheter, essaiPret, finalizeMutation, verifRequise, etapeCode])

  const doitVerifier = verifRequise === true && etapeCode !== 'ok'

  const submitMutation = useMutation({
    mutationFn: async (values: Form) => {
      // Le schema garantit déjà personalPhone non vide + ≥8 chiffres,
      // pas besoin de re-vérifier ici.
      await updateUserProfile({
        first_name: values.firstName,
        last_name: values.lastName || '',
        // Le cellulaire vérifié est déjà sur le profil ; on ne l'écrase pas.
        ...(etapeCode === 'ok' ? {} : { phone_number: values.personalPhone }),
      })
    },
    onSuccess: () => navigate(`/${locale}/onboarding/langue`),
    onError: (err) => {
      console.error('[Welcome] update profile failed:', err)
      toast.error(t('toasts.onboarding.profileSaveError'))
    },
  })

  const onSubmit = handleSubmit((values) => submitMutation.mutate(values))
  const saving = submitMutation.isPending
  const finalizing = finalizeMutation.isPending

  // Le numéro s'affiche aussi quand on recharge la page : il est déjà sur le compte.
  const numeroAffiche = callizyNumber || compagnie?.phone_number || ''
  // Les vraies capacités du numéro : un sans-frais n'envoie des textos
  // qu'une fois l'entreprise vérifiée auprès des opérateurs.
  const { data: lignes } = useCompanyPhoneNumbers(compagnie?.id)
  const ligne = lignes?.phone_numbers.find((l) => l.phone_number === numeroAffiche)
  const etatTextos: EtatTextos = !ligne?.is_toll_free
    ? 'actif'
    : ligne.tollfree_sms_status === 'approved'
      ? 'actif'
      : ligne.tollfree_sms_status === 'pending'
        ? 'verification'
        : 'inactif'

  return (
    <OnboardingNavyShell
      deconnexion={false}
      entete={<Avancement etape={1} />}
      // Pendant la vérification du cellulaire, le numéro n'est pas encore là : un
      // cadre vide « Activation… » détournait l'œil du seul geste à faire (30 sept 2026).
      panneau={doitVerifier ? undefined : (
        <ApercuNumero
          locale={locale}
          numero={numeroAffiche ? formatNumber(numeroAffiche) : ''}
          activation={finalizing && !callizyNumber}
          sansFrais={!!ligne?.is_toll_free}
          textos={etatTextos}
        />
      )}
    >
      <main className="flex-grow flex items-center justify-center px-5 py-6 md:py-10">
        <div className="w-full max-w-[500px]">
          <h1 className="text-[28px] md:text-[34px] font-semibold leading-[1.15] tracking-[-0.02em] mb-4 text-balance">
            {doitVerifier ? t('onboarding.welcome.verif.title') : t('onboarding.welcome.title')}
          </h1>

          {doitVerifier ? (
            <div className="mb-10" />
          ) : (
          <div className="mb-10 overflow-hidden lg:hidden">
            {finalizing && !callizyNumber ? (
              <div className="h-16 flex items-center gap-3">
                <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                <p className="text-sm" style={{ color: 'rgba(148,163,184,0.7)' }}>
                  {t('onboarding.welcome.activating')}
                </p>
              </div>
            ) : numeroAffiche ? (
              <div className="text-4xl sm:text-5xl font-semibold tracking-[-0.02em] tabular-nums whitespace-nowrap">
                {formatNumber(numeroAffiche)}
              </div>
            ) : carteManquante ? null : (
              <div className="h-16 flex items-center">
                <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
          )}

          {error && <p className="text-red-400 text-sm mb-6">{error}</p>}

          {carteManquante && (
            <div
              className="mb-8 rounded-xl p-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <p className="text-[15px] leading-relaxed">
                {t('onboarding.welcome.carte.texte', {
                  numero: compagnie?.preferred_phone_number ? formatNumber(compagnie.preferred_phone_number) : '',
                })}
              </p>
              <button
                type="button"
                onClick={() => setCarteOuverte(true)}
                className="mt-4 h-11 px-6 rounded-xl text-[15px] font-semibold transition-all hover:bg-white/90"
                style={{ backgroundColor: 'white', color: '#0A192F' }}
              >
                {t('onboarding.welcome.carte.bouton')}
              </button>
            </div>
          )}
          <UpdateCardDialog
            open={carteOuverte}
            onClose={() => setCarteOuverte(false)}
            darkMode
            onSuccess={async () => {
              setCarteOuverte(false)
              setCarteManquante(false)
              await queryClient.invalidateQueries({ queryKey: ['payment-method'] })
              finalizeMutation.mutate()
            }}
          />

          {!doitVerifier && (
            <p className="text-[15px] mb-8" style={{ color: '#8B9BB4' }}>
              {t('onboarding.welcome.subtitle')}
            </p>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col text-left gap-2">
                <label className="text-xs font-bold tracking-widest px-1" style={{ color: '#94A3B8' }}>
                  {t('onboarding.welcome.firstNameLabel')}
                </label>
                <input
                  type="text"
                  placeholder={t('onboarding.welcome.firstNamePlaceholder')}
                  className="w-full h-12 px-4 rounded-lg outline-none transition-all"
                  style={{
                    backgroundColor: '#112240',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'white',
                  }}
                  {...register('firstName')}
                />
                {errors.firstName && (
                  <span className="text-xs text-red-400">{errors.firstName.message}</span>
                )}
              </div>
              <div className="flex flex-col text-left gap-2">
                <label className="text-xs font-bold tracking-widest px-1" style={{ color: '#94A3B8' }}>
                  {t('onboarding.welcome.lastNameLabel')}
                </label>
                <input
                  type="text"
                  placeholder={t('onboarding.welcome.lastNamePlaceholder')}
                  className="w-full h-12 px-4 rounded-lg outline-none transition-all"
                  style={{
                    backgroundColor: '#112240',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'white',
                  }}
                  {...register('lastName')}
                />
              </div>
            </div>

            <div className="flex flex-col text-left gap-2">
              <label className="text-xs font-bold tracking-widest px-1" style={{ color: '#94A3B8' }}>
                {t('onboarding.welcome.phoneLabel')}
              </label>
              <PhoneInput
                defaultCountry="ca"
                disabled={etapeCode !== 'idle' && verifRequise === true}
                value={personalPhone || ''}
                onChange={(v) => setValue('personalPhone', v)}
                style={{ '--react-international-phone-height': '48px' } as React.CSSProperties}
                inputStyle={{
                  backgroundColor: '#112240',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderLeft: 'none',
                  color: 'white',
                  width: '100%',
                  fontSize: '14px',
                  borderRadius: '0 8px 8px 0',
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    backgroundColor: '#112240',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    paddingLeft: '12px',
                    paddingRight: '8px',
                  },
                  dropdownStyleProps: {
                    style: {
                      backgroundColor: '#112240',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      color: 'white',
                    },
                  },
                }}
              />
              {errors.personalPhone && (
                <span className="text-xs text-red-400">{errors.personalPhone.message}</span>
              )}
              {verifRequise && etapeCode === 'ok' && (
                <span className="text-xs" style={{ color: '#34D399' }}>{t('onboarding.welcome.verif.verified')}</span>
              )}
            </div>

            {doitVerifier && (
              <PhoneVerification phone={personalPhone || ''} locale={locale} etape={etapeCode} onEtape={setEtapeCode} />
            )}

            <div className="mt-14 pt-6 border-t border-white/[0.06] flex justify-end">
              <button
                type="submit"
                // The number must be bought first: the setup configures it.
                disabled={saving || isSubmitting || doitVerifier || verifRequise === null || !numeroAffiche}
                className="w-full sm:w-auto h-11 px-7 rounded-xl text-[15px] font-semibold transition-all hover:bg-white/90 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-35"
                style={{ backgroundColor: 'white', color: '#0A192F' }}
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-[#0A192F]/20 border-t-[#0A192F] rounded-full animate-spin" />
                ) : (
                  t('onboarding.welcome.continue')
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </OnboardingNavyShell>
  )
}
